import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, FileText, Database, Layers, PanelLeft, PanelRight } from 'lucide-react';
import { useUIStore } from '../../store/uiStore';
import Sidebar from './Sidebar';
import config from '../../config';
import logoSmall from '../../assets/images/logo_64x64.png';

const RAIL_WIDTH = 56;
const SIDEBAR_WIDTH = 320;

const LeftRail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, toggleSidebar } = useUIStore();

  const path = location.pathname;
  const active =
    path.startsWith('/chat') || path.startsWith('/folder')
      ? 'chat'
      : path.startsWith('/global-documents') || path.startsWith('/document')
        ? 'documents'
        : path.startsWith('/data-sources')
          ? 'sources'
          : path.startsWith('/knowledge-base')
            ? 'knowledge'
            : '';

  const navItems = [
    { id: 'chat', label: 'Chat', icon: MessageSquare, path: '/chat' },
    { id: 'sources', label: 'Data Sources', icon: Database, path: '/data-sources' },
    { id: 'knowledge', label: 'Knowledge Base', icon: Layers, path: '/knowledge-base' },
    { id: 'documents', label: 'Global Documents', icon: FileText, path: '/global-documents' },
  ];

  return (
    <aside className="fixed inset-y-0 left-0 z-50 w-14 border-r border-gray-200 bg-white">
      <div className="flex h-full flex-col items-center py-2">
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="mb-2 flex h-9 w-9 items-center justify-center rounded-md border border-primary-200 bg-primary-50 hover:bg-primary-100"
          title={config.appName}
          aria-label="Go to chat"
        >
          <img src={logoSmall} alt={`${config.appName} logo`} className="h-5 w-5" />
        </button>

        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              className={`relative mt-1 flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                isActive
                  ? 'bg-gray-100 text-primary-700'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title={item.label}
              aria-label={item.label}
            >
              {isActive && <span className="absolute left-0 top-2 h-5 w-0.5 rounded bg-primary-600" />}
              <Icon className="h-4 w-4" />
            </button>
          );
        })}

        <div className="flex-1" />

        <button
          type="button"
          onClick={toggleSidebar}
          className="mb-1 flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
        >
          {sidebarOpen ? <PanelLeft className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
};

const AppLayout: React.FC = () => {
  const {
    sidebarOpen,
    isMobile,
    toggleSidebar,
    setIsMobile,
  } = useUIStore();
  
  // Handle responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsMobile]);

  return (
    <div className="flex h-screen bg-gray-50">
      {!isMobile && <LeftRail />}
      {isMobile && (
        <button
          type="button"
          onClick={toggleSidebar}
          className="fixed left-3 top-3 z-50 flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-800"
          title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
        >
          {sidebarOpen ? <PanelLeft className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
        </button>
      )}

      {/* Sidebar - z-40 so it's above content but below some modals */}
      <div
        className={`fixed inset-y-0 z-40 bg-white shadow-xl transition-transform duration-300 ease-in-out ${
          sidebarOpen 
            ? 'translate-x-0 w-80' 
            : '-translate-x-full'
        } ${isMobile ? 'left-0 w-80' : 'left-14 w-80'}`}
      >
        {sidebarOpen && <Sidebar />}
      </div>

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
          onClick={toggleSidebar}
        />
      )}

      {/* Main content area - Push content over when sidebar is visible (desktop only). min-w-0 + overflow-x-hidden so header never scrolls into sidebar. */}
      <div 
        className="flex-1 flex flex-col min-w-0 overflow-x-hidden transition-all duration-300"
        style={{
          marginLeft: isMobile ? 0 : (sidebarOpen ? RAIL_WIDTH + SIDEBAR_WIDTH : RAIL_WIDTH),
        }}
      >
        {/* Main content - vertical scroll only; no horizontal scroll so header stays fixed */}
        <div className="flex-1 flex min-h-0 min-w-0">
          <main className="flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
